import { useState, useEffect, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Spinner } from 'reactstrap';
import DOMPurify from 'dompurify';
import { getChatbotPublicConfig, sendChatbotMessage } from '../ChatBot/Components/FormikHelpers/Api';
import {
    getOrCreateSessionId,
    parseMarkdown,
    buildUserMessage,
    buildBotMessage,
    buildTypingMessage,
    FIELD_STATUS_COLORS,
} from '../ChatBot/Components/FormikHelpers';
import './chat-widget.scss';

const PURIFY_CONFIG = {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'br', 'p', 'ul', 'ol', 'li', 'a', 'span'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
    ALLOWED_URI_REGEXP: /^(https?:|mailto:|tel:)/i,
};
const safeHtml = (raw) => DOMPurify.sanitize(parseMarkdown(raw || ''), PURIFY_CONFIG);

const getParentOrigin = () => {
    try {
        if (document.referrer) {
            return new URL(document.referrer).origin;
        }
    } catch (_) {}
    return window.location.origin;
};

const PublicChat = () => {
    const history = useHistory();
    const query = new URLSearchParams(history.location.search);
    const token = query.get('token');

    const [config, setConfig] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [inputVal, setInputVal] = useState('');
    const [formData, setFormData] = useState({});
    const [activeFormId, setActiveFormId] = useState(null);
    const [initialized, setInitialized] = useState(false);

    const bottomRef = useRef(null);
    const sessionId = useRef(getOrCreateSessionId());

    useEffect(() => {
        if (!token) return;
        (async () => {
            try {
                const res = await getChatbotPublicConfig(token);
                setConfig(res.data);
                try {
                    window.parent.postMessage(
                        {
                            __rdcb_source__: 'chatbot',
                            type: 'branding',
                            logo_icon: res.data?.logo_icon || null,
                            logo_icon_url: res.data?.logo_icon_url || null,
                            launcher_icon: res.data?.launcher_icon || null,
                            launcher_icon_url: res.data?.launcher_icon_url || null,
                            closing_icon: res.data?.closing_icon || null,
                            closing_icon_url: res.data?.closing_icon_url || null,
                            primary_color: res.data?.primary_color || null,
                            accent_color: res.data?.secondary_color || null,
                            chat_window_width: res.data?.chat_window_width || null,
                            chat_window_height: res.data?.chat_window_height || null,
                            position: res.data?.position || null,
                            offset_x: res.data?.offset_x ?? null,
                            offset_y: res.data?.offset_y ?? null,
                            font_family: res.data?.font_family || null,
                        },
                        getParentOrigin()
                    );
                } catch (_) { }
            } catch (_) { }
        })();
    }, [token]);

    useEffect(() => {
        if (config && !initialized) {
            setInitialized(true);
            sendIntent('greeting');
        }
    }, [config]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const primary = config?.primary_color || '#1D4ED8';
    const secondary = config?.secondary_color || config?.accent_color || '#3B82F6';
    const gradient = `linear-gradient(135deg, ${primary}, ${secondary})`;

    const sendIntent = useCallback(
        async (intent, payload = {}, branchData = null) => {
            if (!token) return;
            setLoading(true);
            const typingMsg = buildTypingMessage();
            setMessages((prev) => [...prev, typingMsg]);

            try {
                const requestBody = {
                    token,
                    session_id: sessionId.current,
                    intent,
                    payload,
                };

                if (branchData?.branch_variable && branchData?.branch_value) {
                    requestBody.branch_variable = branchData.branch_variable;
                    requestBody.branch_value = branchData.branch_value;
                }

                const res = await sendChatbotMessage(requestBody);
                const botMsg = buildBotMessage(res.data);
                setMessages((prev) => [...prev.filter((m) => m.id !== typingMsg.id), botMsg]);
                if (res.data.form_fields || res.data.type === 'form') setActiveFormId(botMsg.id);

                try {
                    window.parent.postMessage({ __rdcb_source__: 'chatbot', type: 'new_message' }, getParentOrigin());
                } catch (_) { }
            } catch (_) {
                setMessages((prev) => prev.filter((m) => m.id !== typingMsg.id));
            } finally {
                setLoading(false);
            }
        },
        [token],
    );

    const handleOptionClick = useCallback(
        (option) => {
            const label = `${option.icon ? option.icon + ' ' : ''}${option.label}`;
            setMessages((prev) => [...prev, buildUserMessage(label)]);

            const branchData =
                option.branch_variable && option.branch_value
                    ? { branch_variable: option.branch_variable, branch_value: option.branch_value }
                    : null;

            sendIntent(option.intent, {}, branchData);
        },
        [sendIntent],
    );

    const handleSend = useCallback(() => {
        const text = inputVal.trim();
        if (!text || loading) return;
        setMessages((prev) => [...prev, buildUserMessage(text)]);
        setInputVal('');
        sendIntent('ai_fallback', { message: text });
    }, [inputVal, loading, sendIntent]);

    const handleFormSubmit = useCallback(
        (msgId, formFields, submitIntent) => {
            for (const f of formFields.filter((f) => f.required)) {
                if (!formData[f.name]?.toString().trim()) {
                    alert(`"${f.label}" is required`);
                    return;
                }
            }
            setActiveFormId(null);
            const intent =
                submitIntent ||
                (formFields.some((f) => f.name === 'unit_number') ? 'suite_info_lookup' : 'lead_capture_submit');
            const summary = formFields
                .filter((f) => formData[f.name])
                .map((f) => `${f.label}: ${formData[f.name]}`)
                .join(' · ');
            setMessages((prev) => [...prev, buildUserMessage(summary || 'Submitted')]);
            sendIntent(intent, formData);
            setFormData({});
        },
        [formData, sendIntent],
    );

    const renderFormField = (field, msgId) => {
        const val = formData[field.name] || '';
        const onChange = (e) => setFormData((d) => ({ ...d, [field.name]: e.target.value }));

        const sharedProps = {
            value: val,
            onChange,
            onFocus: (e) => (e.target.style.borderColor = primary),
            onBlur: (e) => (e.target.style.borderColor = ''),
        };

        return (
            <div key={field.name} className="cb-form__field">
                <label className="cb-form__label">
                    {field.label}
                    {field.required && <span className="cb-form__required">*</span>}
                </label>

                {field.type === 'textarea' ? (
                    <textarea
                        rows={3}
                        className="cb-form__textarea"
                        {...sharedProps}
                        placeholder={field.placeholder || ''}
                    />
                ) : field.type === 'select' ? (
                    <select className="cb-form__input" value={val} onChange={onChange}>
                        <option value="">Select…</option>
                        {(field.options || []).map((o) => (
                            <option key={o} value={o}>
                                {o}
                            </option>
                        ))}
                    </select>
                ) : field.type === 'radio' ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
                        {(field.options || []).map((o) => (
                            <label
                                key={o}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 5,
                                    fontSize: 12,
                                    cursor: 'pointer',
                                    padding: '4px 10px',
                                    borderRadius: 20,
                                    border: `1.5px solid ${val === o ? primary : '#E2E5EE'}`,
                                    background: val === o ? `${primary}15` : '#fff',
                                    color: val === o ? primary : '#334155',
                                    fontWeight: val === o ? 600 : 400,
                                }}
                            >
                                <input
                                    type="radio"
                                    name={`${msgId}_${field.name}`}
                                    value={o}
                                    checked={val === o}
                                    onChange={() => setFormData((d) => ({ ...d, [field.name]: o }))}
                                    style={{ display: 'none' }}
                                />
                                {o}
                            </label>
                        ))}
                    </div>
                ) : field.type === 'checkbox' ? (
                    <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 12 }}>
                        <input
                            type="checkbox"
                            checked={!!val}
                            onChange={(e) =>
                                setFormData((d) => ({ ...d, [field.name]: e.target.checked ? 'yes' : '' }))
                            }
                        />
                        {field.placeholder || field.label}
                    </label>
                ) : (
                    <input
                        type={field.type}
                        className="cb-form__input"
                        {...sharedProps}
                        placeholder={field.placeholder || ''}
                    />
                )}
            </div>
        );
    };

    const renderMessage = (msg) => {
        if (msg.from === 'user') {
            return (
                <div key={msg.id} className="cb-bubble cb-bubble--user" style={{ background: gradient }}>
                    {msg.content}
                </div>
            );
        }

        if (msg.type === 'typing') {
            return (
                <div key={msg.id} className="cb-typing">
                    <div className="cb-typing__dot" />
                    <div className="cb-typing__dot" />
                    <div className="cb-typing__dot" />
                </div>
            );
        }

        return (
            <div key={msg.id} style={{ alignSelf: 'flex-start', maxWidth: '92%', width: '100%' }}>
                {msg.pre_content && (
                    <div
                        className="cb-bubble cb-bubble--bot"
                        dangerouslySetInnerHTML={{ __html: safeHtml(msg.pre_content) }}
                    />
                )}

                {msg.content && msg.type !== 'card' && (
                    <div
                        className="cb-bubble cb-bubble--bot"
                        dangerouslySetInnerHTML={{ __html: safeHtml(msg.content) }}
                    />
                )}

                {msg.options?.length > 0 && (
                    <div className="cb-options">
                        {msg.options.map((opt, idx) => (
                            <button
                                key={opt.intent || idx}
                                className="cb-option-btn"
                                style={{ borderColor: primary, color: primary }}
                                onClick={() => handleOptionClick(opt)}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = primary;
                                    e.currentTarget.style.color = '#fff';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = '#fff';
                                    e.currentTarget.style.color = primary;
                                }}
                            >
                                {opt.icon ? <span style={{ marginRight: 4 }}>{opt.icon}</span> : null}
                                {opt.label}
                            </button>
                        ))}
                    </div>
                )}

                {msg.type === 'card' && msg.fields && (
                    <div className="cb-card">
                        {msg.content && (
                            <div
                                className="cb-card__header"
                                style={{ background: `${primary}15`, color: primary }}
                            >
                                <span dangerouslySetInnerHTML={{ __html: safeHtml(msg.content) }} />
                            </div>
                        )}
                        {msg.fields.map((f, idx) => {
                            if (f.isDescription) {
                                return (
                                    <div key="desc" className="cb-card__body">
                                        {f.value}
                                    </div>
                                );
                            }
                            const st = FIELD_STATUS_COLORS[f.status] || FIELD_STATUS_COLORS.neutral;
                            return (
                                <div key={f.label || idx} className="cb-card__row">
                                    <span className="cb-card__label">{f.label}</span>
                                    <span
                                        className={`cb-card__value cb-card__value--${f.status || 'neutral'}`}
                                        style={{ color: st.color, background: st.background }}
                                    >
                                        {f.value}
                                    </span>
                                </div>
                            );
                        })}
                        {msg.cta && (
                            <a
                                href={msg.cta.url}
                                target="_blank"
                                rel="noreferrer"
                                className="cb-card__cta"
                                style={{ background: gradient }}
                            >
                                {msg.cta.label} →
                            </a>
                        )}
                    </div>
                )}

                {msg.type === 'link' && msg.cta && (
                    <a
                        href={msg.cta.url}
                        target="_blank"
                        rel="noreferrer"
                        className="cb-link-cta"
                        style={{ background: gradient }}
                    >
                        {msg.cta.label} →
                    </a>
                )}

                {msg.type === 'form' && msg.form_fields && activeFormId === msg.id && (
                    <div className="cb-form">
                        {[...(msg.form_fields || [])]
                            .sort((a, b) => (a.order || 0) - (b.order || 0))
                            .map((field) => renderFormField(field, msg.id))}
                        <button
                            className="cb-form__submit"
                            style={{ background: gradient }}
                            onClick={() => handleFormSubmit(msg.id, msg.form_fields, msg.submit_intent)}
                        >
                            Submit →
                        </button>
                    </div>
                )}
            </div>
        );
    };

    if (!token) {
        return (
            <div style={{ padding: 24, fontFamily: 'sans-serif', color: '#EF4444' }}>
                Invalid or missing token.
            </div>
        );
    }

    const logoUrl = config?.logo_icon_url || null;
    const botName = config?.bot_name || 'Assistant';
    const placeholderText = config?.placeholder_text || 'Type a message…';
    const fontFamily = config?.font_family || 'Inter, system-ui, sans-serif';

    return (
        <div className="cb-public-root" style={{ fontFamily }}>
            <style>{`
                .cb-option-btn { border-color: ${primary}; color: ${primary}; }
                .cb-option-btn:hover { background: ${primary} !important; color: #fff !important; }
                .cb-send-btn { background: ${gradient}; }
                .cb-text-input:focus { border-color: ${primary}; box-shadow: 0 0 0 3px ${primary}22; }
            `}</style>

            <div className="cb-header" style={{ background: gradient }}>
                <div className="cb-header__avatar">
                    {logoUrl ? (
                        <img
                            src={logoUrl}
                            alt="bot"
                            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                        />
                    ) : (
                        config?.logo_icon || '🏠'
                    )}
                </div>
                <div className="cb-header__meta">
                    <p className="cb-header__name">{botName}</p>
                    <p className="cb-header__status">Online · Typically replies instantly</p>
                </div>
            </div>

            <div className="cb-messages">
                {messages.map(renderMessage)}
                <div ref={bottomRef} />
            </div>

            <div className="cb-input-row">
                <input
                    className="cb-text-input"
                    placeholder={placeholderText}
                    value={inputVal}
                    onChange={(e) => setInputVal(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                />
                <button
                    className="cb-send-btn"
                    style={{ background: gradient }}
                    onClick={handleSend}
                    disabled={loading}
                    aria-label="Send"
                >
                    {loading ? <Spinner size="sm" style={{ width: 14, height: 14 }} /> : '➤'}
                </button>
            </div>
        </div>
    );
};

export { PublicChat };